import React, { useState, useEffect } from 'react';
import { Shield, Users, Calendar, Video, Book, Bell, Settings, Home, BarChart2, Database, FileText, MessageSquare, BookOpenCheck, HelpingHand, Building, Globe, ChevronDown, Menu, X } from 'lucide-react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { getUserRole } from '../../lib/auth';

interface AdminGlobalLayoutProps {
  children: React.ReactNode;
  title: string;
}

interface Igreja {
  id: string;
  nome: string;
  cidade: string;
  estado: string;
}

const menuItems = [
  { icon: BarChart2, label: 'Dashboard', path: '/admin-global' },
  { icon: Users, label: 'Usuários', path: '/admin-global/users' },
  { icon: Building, label: 'Igrejas', path: '/admin-global/igrejas' },
  { icon: Calendar, label: 'Eventos', path: '/admin-global/events' },
  { icon: Video, label: 'Transmissões', path: '/admin-global/live' },
  { icon: Book, label: 'Mensagens', path: '/admin-global/messages' },
  { icon: MessageSquare, label: 'Pedidos de Oração', path: '/admin-global/prayers' },
  { icon: HelpingHand, label: 'Orientação Espiritual', path: '/admin-global/spiritual-guidance' },
  { icon: Bell, label: 'Notificações', path: '/admin-global/notifications' },
  { icon: BookOpenCheck, label: 'Dicas Cristãs', path: '/admin-global/christian-tips' },
  { icon: MessageSquare, label: 'Testemunhos', path: '/admin-global/testimonials' },
  { icon: Database, label: 'Backup', path: '/admin-global/backup' },
  { icon: FileText, label: 'Logs', path: '/admin-global/logs' },
  { icon: Settings, label: 'Configurações', path: '/admin-global/settings' },
];

export default function AdminGlobalLayout({ children, title }: AdminGlobalLayoutProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const [igrejas, setIgrejas] = useState<Igreja[]>([]);
  const [selectedIgreja, setSelectedIgreja] = useState<string>('todas');
  const [igrejaDropdownOpen, setIgrejaDropdownOpen] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [userRole, setUserRole] = useState<string | null>(null);

  useEffect(() => {
    const checkAuth = async () => {
      try {
        const role = await getUserRole();
        setUserRole(role);
        
        if (role !== 'admin' && role !== 'admin_global') {
          console.error("Acesso não autorizado ao painel global");
          navigate('/');
          return;
        }
        
        fetchIgrejas();
      } catch (error) {
        console.error("Erro ao verificar autenticação:", error);
        navigate('/auth');
      }
    };
    
    checkAuth();
  }, [navigate]);

  async function fetchIgrejas() {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('igrejas')
        .select('id, nome, cidade, estado')
        .order('nome');

      if (error) throw error;
      setIgrejas(data || []);
    } catch (error) {
      console.error('Erro ao buscar igrejas:', error);
    } finally {
      setLoading(false);
    }
  }

  const handleIgrejaChange = (id: string) => {
    setSelectedIgreja(id);
    setIgrejaDropdownOpen(false);
    // Aqui você pode implementar a lógica para filtrar os dados com base na igreja selecionada
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Mobile sidebar backdrop */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        ></div>
      )}

      <div className="flex">
        {/* Sidebar - Mobile (off-canvas) */}
        <aside 
          className={`fixed inset-y-0 left-0 z-50 w-64 bg-white border-r border-gray-200 transform transition-transform duration-300 ease-in-out lg:relative lg:translate-x-0 ${
            sidebarOpen ? 'translate-x-0' : '-translate-x-full'
          }`}
        >
          <div className="p-6">
            <div className="flex items-center justify-between mb-8">
              <div className="flex items-center gap-3">
                <Globe className="h-8 w-8 text-brand-teal" />
                <span className="font-semibold text-gray-900">Admin Global</span>
              </div>
              <button 
                className="lg:hidden text-gray-500 hover:text-gray-700"
                onClick={() => setSidebarOpen(false)}
              >
                <X className="h-6 w-6" />
              </button>
            </div>

            {/* Igreja selector */}
            <div className="mb-6 relative">
              <button
                onClick={() => setIgrejaDropdownOpen(!igrejaDropdownOpen)}
                className="w-full flex items-center justify-between p-3 bg-brand-light text-brand-teal rounded-lg hover:bg-brand-light/80 transition-colors"
              >
                <div className="flex items-center gap-2">
                  <Building className="h-5 w-5" />
                  <span className="font-medium">
                    {selectedIgreja === 'todas' 
                      ? 'Todas as Igrejas' 
                      : igrejas.find(i => i.id === selectedIgreja)?.nome || 'Selecione uma Igreja'}
                  </span>
                </div>
                <ChevronDown className="h-4 w-4" />
              </button>

              {igrejaDropdownOpen && (
                <div className="absolute left-0 right-0 mt-2 bg-white border border-gray-200 rounded-lg shadow-lg z-10 max-h-60 overflow-y-auto">
                  <button
                    onClick={() => handleIgrejaChange('todas')}
                    className={`w-full text-left px-4 py-2 hover:bg-gray-50 ${
                      selectedIgreja === 'todas' ? 'bg-brand-light text-brand-teal' : 'text-gray-700'
                    }`}
                  >
                    Todas as Igrejas
                  </button>
                  
                  {loading ? (
                    <div className="p-4 text-center text-gray-500">
                      Carregando...
                    </div>
                  ) : igrejas.length > 0 ? (
                    igrejas.map(igreja => (
                      <button
                        key={igreja.id}
                        onClick={() => handleIgrejaChange(igreja.id)}
                        className={`w-full text-left px-4 py-2 hover:bg-gray-50 ${
                          selectedIgreja === igreja.id ? 'bg-brand-light text-brand-teal' : 'text-gray-700'
                        }`}
                      >
                        {igreja.nome} - {igreja.cidade}/{igreja.estado}
                      </button>
                    ))
                  ) : (
                    <div className="p-4 text-center text-gray-500">
                      Nenhuma igreja encontrada
                    </div>
                  )}
                </div>
              )}
            </div>

            <nav className="space-y-1">
              {menuItems.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                    location.pathname === item.path
                      ? 'bg-brand-light text-brand-teal'
                      : 'text-gray-600 hover:bg-gray-50'
                  }`}
                  onClick={() => setSidebarOpen(false)}
                >
                  <item.icon className="h-5 w-5" />
                  <span>{item.label}</span>
                </Link>
              ))}
            </nav>
          </div>
        </aside>

        {/* Main Content */}
        <div className="flex-1 flex flex-col min-w-0">
          {/* Header */}
          <header className="bg-white border-b border-gray-200 px-4 lg:px-8 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <button 
                  className="lg:hidden p-2 text-gray-500 hover:text-gray-700"
                  onClick={() => setSidebarOpen(true)}
                >
                  <Menu className="h-6 w-6" />
                </button>
                <h1 className="text-xl lg:text-2xl font-bold text-brand-teal">{title}</h1>
              </div>
              <div className="flex items-center gap-4">
                <button
                  onClick={() => navigate('/')}
                  className="flex items-center gap-2 px-4 py-2 bg-brand-teal text-white rounded-lg hover:bg-brand-teal/90 transition-colors"
                >
                  <Home className="h-4 w-4" />
                  <span className="hidden sm:inline">Voltar para o Site</span>
                </button>
              </div>
            </div>
          </header>

          {/* Content */}
          <main className="flex-1 p-4 lg:p-8 overflow-x-auto">
            {children}
          </main>
        </div>
      </div>
    </div>
  );
}